import type { ElementNode, ParagraphNode } from 'lexical';
import { $createParagraphNode, $createTextNode } from 'lexical';

import { prefixMap } from 'components/molecules/LexicalEditor/features/action/constants';
import { $createActionNode } from 'components/molecules/LexicalEditor/features/action/nodes/ActionNode';

/**
 * Given raw text, creates relevant nodes following Lexical structure.
 * Based on the PlainTextPlugin, all lines are placed in a single paragraph node with LineBreakNodes between them.
 * Special patterns like {{prefix:id}} are converted to action nodes if they match known prefixes.
 *
 * @param {string | undefined} text - Raw text input to deserialize
 * @example
 * ```ts
 * editor.update(() => {
 *   $deserializeFromRawText("hello world\n{{function:foo_test}}")
 * })
 * ```
 * @return {ElementNode[]} Array containing a single paragraph node with all content
 */
export function $deserializeFromRawText(
  text: string | undefined,
): ElementNode[] {
  const paragraphNodes: ParagraphNode[] = [];

  if (!text) {
    paragraphNodes.push($createParagraphNode());
    return paragraphNodes;
  }

  const lines = text.split('\n');

  lines.forEach(line => {
    const paragraphNode = $createParagraphNode();
    const parts = line.split(/({{[^}]+}})/).filter(Boolean);

    if (!parts.length || line === '') {
      paragraphNode.append($createTextNode(line));
    }

    parts.forEach(part => {
      if (part.startsWith('{{') && part.endsWith('}}')) {
        const content = part.slice(2, -2);
        let [prefix, id] = content.split(':');
        prefix = `${prefix}:`;
        const actionType = Object.entries(prefixMap).find(
          ([key]) => key === prefix,
        )?.[1];

        if (actionType) {
          const actionNode = $createActionNode({
            prefix,
            id,
          });
          paragraphNode.append(actionNode);
        } else {
          paragraphNode.append($createTextNode(part));
        }
      } else {
        paragraphNode.append($createTextNode(part));
      }
    });

    paragraphNodes.push(paragraphNode);
  });

  return paragraphNodes;
}
